/* eslint-disable no-console */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
  Typography,
  Box,
  Button,
  LinearProgress,
} from '@mui/material';
import { Close, InsertDriveFile } from '@mui/icons-material';
import { useAlert } from '../../alertProvider';
import ImageItem from './ImageItem';
import { ImageUploadModalProps, UploadedImageType } from './ImageUploadModal.types';
import {
  DEFAULT_MAX_FILE_SIZE_MB,
  DEFAULT_ACCEPTED_FORMATS,
  DEFAULT_ACCEPT_ATTRIBUTE,
  BYTES_TO_MB,
} from './ImageUploadModal.constants';

// Import the document upload SVG icon
import { ReactComponent as DocumentUploadIcon } from '../../../assets/SVG/document-upload.svg';

/**
 * ImageUploadModal Component
 * 
 * A unified, reusable modal for uploading images and PDF files.
 * Supports drag & drop, multiple file selection, validation, and preview.
 * 
 * @example
 * ```tsx
 * <ImageUploadModal
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onSave={(images) => handleSave(images)}
 *   maxFileSizeMB={10}
 *   maxFiles={5}
 * />
 * ```
 */
const ImageUploadModal: React.FC<ImageUploadModalProps> = ({
  open,
  onClose,
  onSave,
  uploadedImages = [],
  onRemove,
  onDeleteImage,
  title,
  acceptedFormats = DEFAULT_ACCEPTED_FORMATS,
  maxFileSizeMB = DEFAULT_MAX_FILE_SIZE_MB,
  maxFiles,
  multiple = true,
  viewMode = false,
  customWidth,
  customHeight,
}) => {
  const { t: tGeneral } = useTranslation('general');
  const { t: tSurgery } = useTranslation('surgery_config');
  const { addAlert } = useAlert();
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [tempUploadedImages, setTempUploadedImages] = useState<UploadedImageType[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [isClearing, setIsClearing] = useState(false);

  // Sync with external uploadedImages when modal opens
  useEffect(() => {
    if (open) {
      setTempUploadedImages(uploadedImages);
    }
  }, [open, uploadedImages]);

  /**
   * Generates accept attribute from acceptedFormats
   */
  const getAcceptAttribute = (): string => {
    const extensionMap: Record<string, string> = {
      'image/png': '.png',
      'image/jpeg': '.jpeg,.jpg',
      'image/jpg': '.jpg',
      'application/pdf': '.pdf',
    };
    
    return acceptedFormats
      .map(format => extensionMap[format] || '')
      .filter(Boolean)
      .join(',') || DEFAULT_ACCEPT_ATTRIBUTE;
  };

  /**
   * Validates and processes uploaded files
   */
  const handleFiles = async (files: File[]) => {
    const validFiles: File[] = [];

    for (const file of files) {
      // Type validation - check MIME type or file extension
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      const isValidType = acceptedFormats.includes(file.type) || 
        (fileExtension === '.jpg' && acceptedFormats.includes('image/jpeg')) ||
        (fileExtension === '.jpeg' && acceptedFormats.includes('image/jpeg')) ||
        (fileExtension === '.png' && acceptedFormats.includes('image/png')) ||
        (fileExtension === '.pdf' && acceptedFormats.includes('application/pdf'));
      
      if (!isValidType) {
        addAlert(tSurgery('onlyImageFilesAllowed'), 'error');
        continue;
      }

      // Size validation
      if (file.size > maxFileSizeMB * BYTES_TO_MB) {
        addAlert(tSurgery('fileSizeExceedsLimit', { limit: maxFileSizeMB }), 'error');
        continue;
      }

      // Max files validation
      if (maxFiles && tempUploadedImages.length + validFiles.length >= maxFiles) {
        addAlert(tSurgery('maxFilesReached', { max: maxFiles }), 'warning');
        break;
      }

      validFiles.push(file);
    }

    // Clear file input if no valid files
    if (validFiles.length === 0 && files.length > 0) {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Start processing with progress indicator
    setIsProcessing(true);
    setProcessingProgress(0);

    // Convert valid files to Base64 with progress tracking
    const processedImages: UploadedImageType[] = [];
    
    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      try {
        const result = await new Promise<UploadedImageType>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            let base64Result = reader.result as string;
            
            // Fix PDF Data URL if browser doesn't include MIME type
            if (file.type === 'application/pdf' && base64Result.startsWith('data:;base64,')) {
              base64Result = base64Result.replace('data:;base64,', 'data:application/pdf;base64,');
            }

            // Extract file extension (without dot)
            const extension = file.name.split('.').pop()?.toLowerCase() || '';
            
            resolve({
              name: file.name,
              image: base64Result,
              size: (file.size / BYTES_TO_MB).toFixed(2),
              sizeBytes: file.size, // Store size in bytes for API
              file_extension: extension, // Store extension for API
              isExisting: false, // Explicitly mark as new upload
            });
          };
          reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
          reader.readAsDataURL(file);
        });
        
        processedImages.push(result);
        setProcessingProgress(Math.round(((i + 1) / validFiles.length) * 100));
      } catch (error) {
        console.error('Error reading file:', error);
      }
    }

    setTempUploadedImages(prev => [...prev, ...processedImages]);
    setIsProcessing(false);
    setProcessingProgress(0);

    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * Handle file drop
   */
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  /**
   * Handle drag over
   */
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  /**
   * Handle drag leave
   */
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  /**
   * Handle file input change
   */
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    handleFiles(files);
  };

  /**
   * Handle remove image
   */
  const handleRemoveImage = async (index: number) => {
    const imageToRemove = tempUploadedImages[index];
    
    // If it's an existing image with an id, call the delete API
    if (imageToRemove?.isExisting && imageToRemove?.id && onDeleteImage) {
      try {
        await onDeleteImage(imageToRemove.id);
      } catch (error) {
        console.error('Failed to delete image:', error);
        return; // Don't remove from list if API call failed
      }
    }
    
    setTempUploadedImages(prev => {
      const updatedImages = prev.filter((_, i) => i !== index);
      onRemove?.(updatedImages);
      return updatedImages;
    });
  };

  /**
   * Handle clear all files
   * - For existing files: calls delete API for each
   * - For new files: just removes from state
   */
  const handleClearAll = async () => {
    const existingFiles = tempUploadedImages.filter(img => img.isExisting && img.id);
    const hasExistingFiles = existingFiles.length > 0;
    
    // If there are existing files and we have a delete handler, show confirmation
    if (hasExistingFiles && onDeleteImage) {
      const confirmed = window.confirm(
        tSurgery('confirmDeleteFilesFromServer', { count: existingFiles.length })
      );
      if (!confirmed) return;
    }
    
    setIsClearing(true);
    
    try {
      // Delete existing files from server
      if (hasExistingFiles && onDeleteImage) {
        const deletePromises = existingFiles.map(file => 
          onDeleteImage(file.id!).catch(err => {
            console.error(`Failed to delete file ${file.id}:`, err);
            return { failed: true, id: file.id };
          })
        );
        
        const results = await Promise.all(deletePromises);
        const failedDeletes = results.filter(r => r && typeof r === 'object' && 'failed' in r);
        
        if (failedDeletes.length > 0) {
          addAlert(tSurgery('failedToDeleteFiles', { count: failedDeletes.length }), 'warning');
          // Keep only the files that failed to delete
          const failedIds = failedDeletes.map(f => (f as { id: number }).id);
          setTempUploadedImages(prev => prev.filter(img => 
            img.isExisting && img.id && failedIds.includes(img.id)
          ));
          setIsClearing(false);
          return;
        }
      }
      
      // Clear all files from state
      setTempUploadedImages([]);
      onRemove?.([]);
      addAlert(tSurgery('allFilesCleared'), 'success');
    } catch (error) {
      console.error('Error clearing files:', error);
      addAlert(tSurgery('failedToClearFiles'), 'error');
    } finally {
      setIsClearing(false);
    }
  };

  /**
   * Handle save
   */
  const handleSave = () => {
    onSave(tempUploadedImages);
    setTempUploadedImages([]);
    onClose();
  };

  /**
   * Handle close
   */
  const handleClose = () => {
    setTempUploadedImages([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  // Common button styles
  const commonButtonStyles = {
    width: '48%',
    borderRadius: '16px',
    fontWeight: 'bold',
    fontSize: '1.1rem',
    textTransform: 'capitalize' as const,
    transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
    fontFamily: 'Montserrat, sans-serif',
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      fullWidth 
      maxWidth={customWidth ? false : 'sm'}
      PaperProps={{
        sx: {
          ...(customWidth && { width: customWidth, maxWidth: customWidth }),
          ...(customHeight && { height: customHeight, maxHeight: customHeight }),
        },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ fontWeight: 'bold', fontSize: '20px', p: 2, fontFamily: 'Montserrat, sans-serif' }}>
        {title || tSurgery('uploadImage')}
        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          aria-label="close"
        >
          <Close />
        </IconButton>
      </DialogTitle>
      
      <Divider sx={{ borderBottom: '2px solid #E0E0E0' }} />

      {/* Content */}
      <DialogContent sx={{ p: 2 }}>
        {/* Processing Progress Indicator */}
        {isProcessing && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="primary" fontWeight={500} sx={{ fontFamily: 'Nunito, sans-serif' }}>
                {tSurgery('processingFiles')}
              </Typography>
              <Typography variant="body2" color="primary" fontWeight={600} sx={{ fontFamily: 'Nunito, sans-serif' }}>
                {processingProgress}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={processingProgress} 
              sx={{ 
                height: 8, 
                borderRadius: 4,
                backgroundColor: '#E3F2FD',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  backgroundColor: '#1976D2',
                }
              }} 
            />
          </Box>
        )}

        {/* Drop Zone (hidden in view mode) */}
        {!viewMode && (
          <Box
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => !isProcessing && fileInputRef.current?.click()}
            sx={{
              width: '100%',
              minHeight: 200,
              border: isDragging ? '2px dashed #E17E61' : '1.5px dashed #D0D5DD',
              borderRadius: 3,
              background: isDragging 
                ? 'linear-gradient(180deg, rgba(225, 126, 97, 0.08) 0%, rgba(225, 126, 97, 0.02) 100%)'
                : 'linear-gradient(180deg, #FFFFFF 0%, #FAFAFA 100%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: isProcessing ? 'wait' : 'pointer',
              textAlign: 'center',
              p: 4,
              mb: 2,
              transition: 'all 0.25s ease',
              opacity: isProcessing ? 0.6 : 1,
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                borderColor: isProcessing ? '#D0D5DD' : '#E17E61',
                background: isProcessing 
                  ? 'linear-gradient(180deg, #FFFFFF 0%, #FAFAFA 100%)'
                  : 'linear-gradient(180deg, rgba(225, 126, 97, 0.06) 0%, rgba(225, 126, 97, 0.01) 100%)',
                '& .upload-icon-container': {
                  transform: 'scale(1.05)',
                  backgroundColor: '#FFFFFF',
                },
              },
            }}
          >
            {/* Modern Icon Container */}
            <Box
              className="upload-icon-container"
              sx={{
                width: 72,
                height: 72,
                borderRadius: '16px',
                backgroundColor: isDragging ? '#FFF0EB' : '#FFFFFF',
                border: isDragging ? '1px solid #E17E61' : '1px solid #EAECF0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2.5,
                transition: 'all 0.25s ease',
                boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
              }}
            >
              <DocumentUploadIcon 
                style={{ 
                  width: 32, 
                  height: 32,
                  transition: 'all 0.25s ease',
                }} 
              />
            </Box>
            
            {/* Main Text */}
            <Typography 
              sx={{ 
                fontWeight: 500, 
                color: '#344054', 
                fontSize: '16px',
                mb: 0.5, 
                fontFamily: 'Montserrat, sans-serif',
                letterSpacing: '-0.01em',
              }}
            >
              {isDragging ? tSurgery('dropFilesHere') : tSurgery('dragAndDropFiles')}
            </Typography>
            
            {/* Or Separator */}
            <Typography 
              sx={{ 
                color: '#98A2B3', 
                fontSize: '13px',
                fontFamily: 'Nunito, sans-serif',
                mb: 1.5,
              }}
            >
              {tSurgery('or')}
            </Typography>
            
            {/* Browse Button */}
            <Box
              component="span"
              sx={{ 
                color: '#E17E61', 
                fontWeight: 600, 
                fontSize: '14px',
                fontFamily: 'Montserrat, sans-serif',
                cursor: 'pointer',
                padding: '8px 20px',
                borderRadius: '8px',
                border: '1px solid #E17E61',
                backgroundColor: 'transparent',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: '#E17E61',
                  color: '#FFFFFF',
                },
              }}
            >
              {tSurgery('browseFiles')}
            </Box>
            
            {/* File type info */}
            <Typography 
              sx={{ 
                color: '#98A2B3', 
                fontSize: '12px',
                fontFamily: 'Nunito, sans-serif',
                mt: 2.5,
              }}
            >
              {tSurgery('supportedFormatsInfo', { maxSize: maxFileSizeMB })}
            </Typography>
            
            {maxFiles && (
              <Typography 
                sx={{ 
                  color: '#98A2B3', 
                  fontSize: '12px',
                  fontFamily: 'Nunito, sans-serif',
                  mt: 0.5,
                }}
              >
                {tSurgery('maxFilesAllowed', { count: maxFiles })}
              </Typography>
            )}
            
            <input
              ref={fileInputRef}
              type="file"
              accept={getAcceptAttribute()}
              multiple={multiple}
              hidden
              onChange={handleFileInputChange}
              disabled={isProcessing}
            />
          </Box>
        )}

        {/* Files Count Header */}
        {tempUploadedImages.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333', fontFamily: 'Nunito, sans-serif' }}>
              {tSurgery('uploadedFilesCount', { count: tempUploadedImages.length })}
            </Typography>
            {!viewMode && tempUploadedImages.length > 1 && (
              <Button 
                size="small" 
                color="error" 
                onClick={handleClearAll}
                disabled={isClearing}
                sx={{ fontSize: '12px', fontFamily: 'Nunito, sans-serif' }}
              >
                {isClearing ? tSurgery('clearing') : tGeneral('clearAll')}
              </Button>
            )}
          </Box>
        )}

        {/* Uploaded Files List */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            maxHeight: '300px',
            overflow: 'auto',
          }}
        >
          {tempUploadedImages.map((image, index) => (
            <ImageItem
              key={`${image.name}-${index}`}
              image={image}
              index={index}
              onRemove={handleRemoveImage}
              viewMode={viewMode}
            />
          ))}
        </Box>

        {/* Empty state for view mode */}
        {viewMode && tempUploadedImages.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <InsertDriveFile sx={{ fontSize: 48, color: '#BDBDBD', mb: 1 }} />
            <Typography color="textSecondary" sx={{ fontFamily: 'Nunito, sans-serif' }}>
              {tSurgery('noFilesUploaded')}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <Divider sx={{ borderBottom: '2px solid #E0E0E0' }} />

      {/* Actions */}
      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          sx={{
            ...commonButtonStyles,
            color: '#444',
            borderColor: '#444',
            '&:hover': {
              backgroundColor: '#f5f5f5',
              borderColor: '#222',
              transform: 'scale(1.03)',
            },
          }}
        >
          {viewMode ? tGeneral('close') : tGeneral('Cancel')}
        </Button>

        {!viewMode && (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={tempUploadedImages.length === 0}
            sx={{
              ...commonButtonStyles,
              backgroundColor: '#555',
              color: '#fff',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#333',
                transform: 'scale(1.03)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
              },
              '&.Mui-disabled': {
                backgroundColor: '#bbb',
                color: '#fff',
              },
            }}
          >
            {tGeneral('save')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImageUploadModal;
